"use client";

import * as React from "react";
import { startOfDay } from "date-fns";
import { type DateRange } from "react-day-picker";

import {
  DateRangePicker,
  DateRangePickerTrigger,
  formatRangeDate,
} from "@/components/ui/date-range-picker";
import { TimePicker } from "@/components/ui/time-picker";
import type { BranchOperationHours } from "@/lib/booking/branch-hours";

const dateInputClassName =
  "rounded-l-lg h-12 border border-gray-300 text-md rounded-base focus:ring-brand block w-full cursor-pointer px-3 py-2.5";

const timeInputClassName =
  "rounded-r-lg h-12 border-y border-r border-gray-300 text-md rounded-base focus:ring-brand block w-full px-3 py-2.5";

const fieldLabelClassName =
  "mb-1 block h-5 text-sm font-medium leading-5 text-heading";

export type BookingFormDateRangeProps = {
  variant?: "default" | "drawer";
  range?: DateRange;
  onRangeChange?: (range: DateRange | undefined) => void;
  pickupTime?: number;
  onPickupTimeChange?: (minutes: number) => void;
  returnTime?: number;
  onReturnTimeChange?: (minutes: number) => void;
  pickupOpeningHours?: BranchOperationHours | null;
  returnOpeningHours?: BranchOperationHours | null;
};

export function BookingFormDateRange({
  variant = "default",
  range: controlledRange,
  onRangeChange,
  pickupTime: controlledPickupTime,
  onPickupTimeChange,
  returnTime: controlledReturnTime,
  onReturnTimeChange,
  pickupOpeningHours,
  returnOpeningHours,
}: BookingFormDateRangeProps = {}) {
  const [internalRange, setInternalRange] = React.useState<DateRange | undefined>();
  const [internalPickupTime, setInternalPickupTime] = React.useState<
    number | undefined
  >();
  const [internalReturnTime, setInternalReturnTime] = React.useState<
    number | undefined
  >();

  const range = controlledRange ?? internalRange;
  const setRange = onRangeChange ?? setInternalRange;
  const pickupTime = controlledPickupTime ?? internalPickupTime;
  const setPickupTime = onPickupTimeChange ?? setInternalPickupTime;
  const returnTime = controlledReturnTime ?? internalReturnTime;
  const setReturnTime = onReturnTimeChange ?? setInternalReturnTime;

  const pickerClassName =
    variant === "drawer" ? "flex w-full flex-wrap items-start gap-4" : "contents";
  const minSelectableDate = startOfDay(new Date());

  return (
    <DateRangePicker
      className={pickerClassName}
      value={range}
      onChange={setRange}
      numberOfMonths={2}
      align="start"
      disabled={{ before: minSelectableDate }}
    >
      <div
        className={variant === "drawer" ? "min-w-[168px] flex-1 basis-[168px]" : undefined}
      >
        <label className={fieldLabelClassName}>Pickup date</label>
        <div className="flex">
          <DateRangePickerTrigger
            triggerId="pickup-date"
            className={dateInputClassName}
            value={formatRangeDate(range?.from)}
            placeholder="Date"
            aria-label="Pickup date"
          />
          <TimePicker
            value={pickupTime}
            onChange={setPickupTime}
            title="Select pickup time"
            className={timeInputClassName}
            placeholder="Time"
            id="pickup-time"
            openingStart={pickupOpeningHours?.start}
            openingEnd={pickupOpeningHours?.end}
            disabled={!pickupOpeningHours}
          />
        </div>
      </div>
      <div
        className={variant === "drawer" ? "min-w-[168px] flex-1 basis-[168px]" : undefined}
      >
        <label className={fieldLabelClassName}>Return date</label>
        <div className="flex">
          <DateRangePickerTrigger
            triggerId="return-date"
            className={dateInputClassName}
            value={formatRangeDate(range?.to)}
            placeholder="Date"
            aria-label="Return date"
          />
          <TimePicker
            value={returnTime}
            onChange={setReturnTime}
            title="Select return time"
            className={timeInputClassName}
            placeholder="Time"
            id="return-time"
            openingStart={returnOpeningHours?.start}
            openingEnd={returnOpeningHours?.end}
            disabled={!returnOpeningHours}
          />
        </div>
      </div>
    </DateRangePicker>
  );
}
